import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';

const COLLECTION_NAME = 'events';

/**
 * Create a new event
 * @param {Object} eventData - Event details
 * @returns {Promise<string>} - Event ID
 */
export const createEvent = async (eventData) => {
  try {
    const eventRef = await addDoc(collection(db, COLLECTION_NAME), {
      title: eventData.title,
      description: eventData.description || '',
      organizationId: eventData.organizationId,
      organizationName: eventData.organizationName,
      location: eventData.location || '',
      date: eventData.date, // Should be a Firestore Timestamp
      startTime: eventData.startTime || '',
      endTime: eventData.endTime || '',
      volunteersNeeded: eventData.volunteersNeeded || 0,
      volunteersRegistered: 0,
      assignments: eventData.assignments || [],
      skillsRequired: eventData.skillsRequired || [],
      imageURL: eventData.imageURL || '',
      status: 'upcoming', // 'upcoming' | 'in-progress' | 'completed' | 'cancelled'

      // Recurrence fields
      isRecurring: eventData.isRecurring || false,
      recurrenceId: eventData.recurrenceId || null,
      recurrencePattern: eventData.recurrencePattern || null,
      recurrenceEndDate: eventData.recurrenceEndDate || null,
      monthlyWeekNumber: eventData.monthlyWeekNumber || null,
      monthlyDayOfWeek: eventData.monthlyDayOfWeek || null,
      instanceNumber: eventData.instanceNumber || null,
      totalInstances: eventData.totalInstances || null,

      createdBy: eventData.createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return eventRef.id;
  } catch (error) {
    console.error('Error creating event:', error);
    throw new Error('Failed to create event: ' + error.message);
  }
};

/**
 * Get event by ID
 * @param {string} eventId
 * @returns {Promise<Object>} - Event data with ID
 */
export const getEventById = async (eventId) => {
  try {
    const eventDoc = await getDoc(doc(db, COLLECTION_NAME, eventId));

    if (!eventDoc.exists()) {
      throw new Error('Event not found');
    }

    return {
      eventId: eventDoc.id,
      ...eventDoc.data(),
    };
  } catch (error) {
    console.error('Error getting event:', error);
    throw new Error('Failed to get event: ' + error.message);
  }
};

/**
 * Get events by organization ID, sorted by date (nearest to farthest)
 * @param {string} organizationId
 * @returns {Promise<Array>} - Array of events
 */
export const getEventsByOrganization = async (organizationId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('organizationId', '==', organizationId),
      orderBy('date', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const events = [];

    querySnapshot.forEach((doc) => {
      events.push({
        eventId: doc.id,
        ...doc.data(),
      });
    });

    return events;
  } catch (error) {
    console.error('Error getting events by organization:', error);
    throw new Error('Failed to get events: ' + error.message);
  }
};

/**
 * Get upcoming events by organization ID
 * @param {string} organizationId
 * @returns {Promise<Array>} - Array of upcoming events
 */
export const getUpcomingEventsByOrganization = async (organizationId) => {
  try {
    const now = Timestamp.now();
    const q = query(
      collection(db, COLLECTION_NAME),
      where('organizationId', '==', organizationId),
      where('date', '>=', now),
      orderBy('date', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const events = [];

    querySnapshot.forEach((doc) => {
      events.push({
        eventId: doc.id,
        ...doc.data(),
      });
    });

    return events;
  } catch (error) {
    console.error('Error getting upcoming events:', error);
    throw new Error('Failed to get upcoming events: ' + error.message);
  }
};

/**
 * Get all events (for platform admin)
 * @returns {Promise<Array>} - Array of all events
 */
export const getAllEvents = async () => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      orderBy('date', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const events = [];

    querySnapshot.forEach((doc) => {
      events.push({
        eventId: doc.id,
        ...doc.data(),
      });
    });

    return events;
  } catch (error) {
    console.error('Error getting all events:', error);
    throw new Error('Failed to get events: ' + error.message);
  }
};

/**
 * Update event
 * @param {string} eventId
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateEvent = async (eventId, updates) => {
  try {
    const eventRef = doc(db, COLLECTION_NAME, eventId);
    await updateDoc(eventRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating event:', error);
    throw new Error('Failed to update event: ' + error.message);
  }
};

/**
 * Delete event
 * @param {string} eventId
 * @returns {Promise<void>}
 */
export const deleteEvent = async (eventId) => {
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, eventId));
  } catch (error) {
    console.error('Error deleting event:', error);
    throw new Error('Failed to delete event: ' + error.message);
  }
};

/**
 * Get all events in a recurring series by recurrenceId, sorted by date
 * @param {string} recurrenceId
 * @returns {Promise<Array>} - Array of events
 */
export const getEventsBySeries = async (recurrenceId, organizationId = null) => {
  try {
    const constraints = [where('recurrenceId', '==', recurrenceId)];
    if (organizationId) constraints.push(where('organizationId', '==', organizationId));
    const snapshot = await getDocs(query(collection(db, COLLECTION_NAME), ...constraints));
    return snapshot.docs
      .map(d => ({ eventId: d.id, ...d.data() }))
      .sort((a, b) => (a.date?.toMillis?.() || 0) - (b.date?.toMillis?.() || 0));
  } catch (error) {
    console.error('Error getting events by series:', error);
    throw new Error('Failed to get series events: ' + error.message);
  }
};

/**
 * Delete all events in a recurring series by recurrenceId
 * @param {string} recurrenceId
 * @returns {Promise<number>} - Number of events deleted
 */
export const deleteEventSeries = async (recurrenceId, organizationId) => {
  try {
    const constraints = [where('recurrenceId', '==', recurrenceId)];
    if (organizationId) constraints.push(where('organizationId', '==', organizationId));
    const snapshot = await getDocs(query(collection(db, COLLECTION_NAME), ...constraints));
    await Promise.all(snapshot.docs.map(d => deleteDoc(doc(db, COLLECTION_NAME, d.id))));
    return snapshot.size;
  } catch (error) {
    console.error('Error deleting event series:', error);
    throw new Error('Failed to delete event series: ' + error.message);
  }
};

/**
 * Cancel event
 * @param {string} eventId
 * @returns {Promise<void>}
 */
export const cancelEvent = async (eventId) => {
  try {
    await updateEvent(eventId, {
      status: 'cancelled',
    });
  } catch (error) {
    console.error('Error cancelling event:', error);
    throw new Error('Failed to cancel event: ' + error.message);
  }
};
